/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useDispatch } from 'react-redux';

import moment from 'moment';

import { useRetracked } from 'js/lib/retracked';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { get } from 'js/lib/user';
import { humanizeLearningTime } from 'js/utils/DateTimeUtils';

import { Typography2 } from '@coursera/cds-core';

import { CoachNavigationContainer } from 'bundles/ai-course-coach/components/common';
import type {
  AiCoachNavigationButtons_AiCoach_ContinueLearningNavigationButton_Fragment as ContinueLearningNavigationButton,
  AiCoachNavigationButtons_AiCoach_GoToFirstItemNavigationButton_Fragment as GoToFirstItemNavigationButton,
} from 'bundles/ai-course-coach/queries/__generated__/aiCoachNavigationButtonsFragment';
import { toggleChat } from 'bundles/ai-course-coach/store/actions';
import { generateCourseItemLink } from 'bundles/ai-course-coach/utils/links';
import type { RecommendedNavigationItem } from 'bundles/ai-course-coach/utils/types';

import _t from 'i18n!nls/learning-assistant-chat-ai';

type Props = { navigationButton: ContinueLearningNavigationButton | GoToFirstItemNavigationButton };

const styles = {
  recommendedNavContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
  leadInText: css`
    color: #404b61;
  `,
};

export const RecommendedNavigation = (props: Props): React.ReactElement | null => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const trackRetracked = useRetracked();
  const location = useLocation().pathname;

  const { navigationButton } = props;

  const itemId = navigationButton.itemMetadata.itemId;
  const titleContent = navigationButton.itemMetadata.itemTitle;
  const courseSlug = navigationButton.itemMetadata.course.slug;
  const itemSlug = navigationButton.itemMetadata.itemSlug;
  const currentUser = get();

  let itemType: RecommendedNavigationItem;
  switch (navigationButton.itemMetadata.__typename) {
    case 'AiCoach_ReadingItemMetadata':
      itemType = 'supplement';
      break;
    case 'AiCoach_LectureItemMetadata':
      itemType = 'lecture';
      break;
    // case 'AiCoach_QuizItemMetadata':
    //   itemType = 'exam';
    //   break;
    // case 'AiCoach_AssignmentItemMetadata':
    //   itemType = 'staffGraded';
    //   break;
    default:
      itemType = null;
  }

  if (!itemType) {
    return null;
  }

  let leadInText;
  switch (navigationButton.navigationLeadingMessage) {
    case 'WHY_TAKE_COURSE_NAVIGATION':
      leadInText = _t(
        // eslint-disable-next-line no-restricted-syntax
        "Now that you're familiar with the benefits of this course, let's move forward with your learning."
      );
      break;

    case 'HELP_GET_STARTED_WITH_COURSE_NAVIGATION':
      leadInText = _t("With these tips, you're ready for what's next. Let's dive into the upcoming lesson together.");
      break;

    case 'RECAP_BELOW_BOUNDARY_NAVIGATION':
      leadInText = _t(
        // eslint-disable-next-line no-restricted-syntax
        'Staying actively engaged with your course material is a surefire way to deepen your grasp of the subject. Ready to pick up where you left off?'
      );
      break;

    case 'RECAP_ABOVE_BOUNDARY_NAVIGATION':
      leadInText = _t(
        `Revisiting these concepts can help realign your learning goals and refresh your memory. When you're ready, continue learning with the next lesson. Or you can ask me questions about what you've learned so far.`
      );
      break;

    case 'RECAP_WITHIN_BOUNDARY_NAVIGATION':
      leadInText = _t(
        `Reviewing these concepts solidifies your understanding and sets the stage for continued learning. You're ready to build on what you know and move on to the next item.`
      );
      break;

    default:
      leadInText = null;
  }

  let buttonText;
  switch (navigationButton.__typename) {
    case 'AiCoach_GoToFirstItemNavigationButton':
    default:
      buttonText = _t('Start learning');
      break;

    case 'AiCoach_ContinueLearningNavigationButton':
      buttonText = _t('Continue learning');
      break;
  }

  let metadata: string[];
  switch (navigationButton.itemMetadata.__typename) {
    case 'AiCoach_ReadingItemMetadata':
      metadata = [humanizeLearningTime(moment.duration(navigationButton.itemMetadata.readingDuration))];
      break;
    case 'AiCoach_LectureItemMetadata':
      metadata = [humanizeLearningTime(moment.duration(navigationButton.itemMetadata.lectureDuration))];
      break;
    // case 'AiCoach_QuizItemMetadata':
    //   metadata = [_t('#{numQuestions}', { numQuestions: navigationButton.itemMetadata.numQuestions.toString() })];
    //   break;
    // case 'AiCoach_AssignmentItemMetadata':
    //   metadata = [_t('#{numQuestions}', { numQuestions: navigationButton.itemMetadata.numQuestions.toString() })];
    //   break;
    default:
      metadata = [];
  }

  const handleNavButtonClick = () => {
    const courseItemLink = generateCourseItemLink({ itemType, courseSlug, itemSlug, itemId });

    const sharedEventingData = {
      userLocale: currentUser.locale,
      navMessage: navigationButton.navigationLeadingMessage,
      navButtonType: navigationButton.__typename,
    };

    trackRetracked({
      trackingData: {
        userId: currentUser.id,
        userLocale: sharedEventingData.userLocale,
        timestamp: Date.now(),
        // itemId, // TODO: Add itemId of the current item when we add nav buttons to item pages
        navMessage: sharedEventingData.navMessage,
        navButtonType: sharedEventingData.navButtonType,
        navTargetUrl: courseItemLink,
        referrerUrl: location,
      },
      trackingName: 'ai_course_coach_recommended_navigation',
      action: `click`,
    });

    // v3 tracking
    // track('interact_coach', {
    //   coachInteractionType: 'click_action',
    //   coach: {
    //     mode: trackingContext?.mode,
    //   },
    //   coachAction: 'click_recommended_navigation_button',
    //   ...(trackingContext?.courseId && {
    //     product: {
    //       type: 'course',
    //       id: trackingContext.courseId as string,
    //     },
    //   }),
    //   ...(trackingContext?.itemId && {
    //     courseItem: {
    //       id: trackingContext.itemId as string,
    //     },
    //   }),
    // });

    dispatch(toggleChat());
    navigate(courseItemLink, { replace: false });
  };

  return (
    <div css={styles.recommendedNavContainer}>
      <Typography2 variant="bodyPrimary" component="p" css={styles.leadInText}>
        <strong>{leadInText}</strong>
      </Typography2>

      <CoachNavigationContainer
        itemId={itemId}
        title={{ content: titleContent }}
        buttonText={buttonText}
        itemType={itemType}
        metadata={metadata}
        handleNavButtonClick={handleNavButtonClick}
      />
    </div>
  );
};

export default RecommendedNavigation;
